/* tslint:disable:no-bitwise */
import { Ellipsoid } from "../Ellipsoid";
import { Vector2 } from "three";
import { BaseTilingScheme } from "./BaseTilingScheme";
import { Rectangle } from "../Rectangle";
import { MathUtils } from "../MathUtils";
import { IdentityProjection } from "../projection";
/**
 * @see TilingScheme
 */
export class GeographicTilingScheme extends BaseTilingScheme {
    constructor(options) {
        var _a, _b, _c, _d, _e;
        const ellipsoid = (_a = options === null || options === void 0 ? void 0 : options.ellipsoid) !== null && _a !== void 0 ? _a : Ellipsoid.WGS_84;
        const numRootTilesX = (_b = options === null || options === void 0 ? void 0 : options.numRootTilesX) !== null && _b !== void 0 ? _b : 2;
        const numRootTilesY = (_c = options === null || options === void 0 ? void 0 : options.numRootTilesY) !== null && _c !== void 0 ? _c : 1;
        super(numRootTilesX, numRootTilesY);
        this._projection = new IdentityProjection(ellipsoid);
        this._rectangle = (_e = (_d = options === null || options === void 0 ? void 0 : options.region) === null || _d === void 0 ? void 0 : _d.clone()) !== null && _e !== void 0 ? _e : Rectangle.MAX.intersection(this._projection.region);
    }
    /**
     * @see TilingScheme.ellipsoid
     */
    get ellipsoid() {
        return this._projection.ellipsoid;
    }
    /**
     * @see TilingScheme.rectangle
     */
    get rectangle() {
        return this._rectangle;
    }
    /**
     * @see TilingScheme.projection
     */
    get projection() {
        return this._projection;
    }
    /**
     * @see TilingScheme.rectangleToNativeRectangle
     */
    rectangleToNativeRectangle(rectangle, result = new Rectangle()) {
        const west = MathUtils.radiansToDegrees(rectangle.west);
        const south = MathUtils.radiansToDegrees(rectangle.south);
        const east = MathUtils.radiansToDegrees(rectangle.east);
        const north = MathUtils.radiansToDegrees(rectangle.north);
        return result.set(west, south, east, north);
    }
    /**
     * @see TilingScheme.tileXYToNativeRectangle
     */
    tileXYToNativeRectangle(x, y, level, result = new Rectangle()) {
        this.tileXYToRectangle(x, y, level, result);
        this.rectangleToNativeRectangle(result);
        return result;
    }
    /**
     * @see TilingScheme.tileXYToRectangle
     */
    tileXYToRectangle(x, y, level, result = new Rectangle()) {
        const xTiles = this.getNumberOfXTilesAtLevel(level);
        const xTileWidth = this._rectangle.width / xTiles;
        const yTiles = this.getNumberOfYTilesAtLevel(level);
        const yTileHeight = this._rectangle.height / yTiles;
        const west = x * xTileWidth + this._rectangle.west;
        const east = (x + 1) * xTileWidth + this._rectangle.west;
        const north = this._rectangle.north - y * yTileHeight;
        const south = this._rectangle.north - (y + 1) * yTileHeight;
        return result.set(west, south, east, north);
    }
    /**
     * @see TilingScheme.positionToTileXY
     */
    positionToTileXY(position, level, result = new Vector2()) {
        if (!this._rectangle.contains(position)) {
            throw new Error("Trying to sample tiling scheme outside of it's relevant bounds");
        }
        const xTiles = this.getNumberOfXTilesAtLevel(level);
        const yTiles = this.getNumberOfYTilesAtLevel(level);
        const xTileWidth = this._rectangle.width / xTiles;
        const yTileHeight = this._rectangle.height / yTiles;
        let xTileCoordinate = ((position.longitude - this._rectangle.west) / xTileWidth) | 0;
        if (xTileCoordinate >= xTiles) {
            xTileCoordinate = xTiles - 1;
        }
        let yTileCoordinate = ((this._rectangle.north - position.latitude) / yTileHeight) | 0;
        if (yTileCoordinate >= yTiles) {
            yTileCoordinate = yTiles - 1;
        }
        return result.set(xTileCoordinate, yTileCoordinate);
    }
}
